import * as Yup from 'yup';

import { TimeOffRow, TimeOffDecision } from 'store/api/time-off/types';
import { toDay, dayCount, dayToDate, longRange } from 'utils/time-off';

// TIME OFF (owner 24 Oct 2026; plan §3, §6.2): the same limits as the
// server (5–200 characters for a decline reason, 200 for a note).
export const declineSchema = Yup.object().shape({
  note: Yup.string()
    .trim()
    .required('Required, 5–200 characters. The rep sees this.')
    .min(5, 'Required, 5–200 characters. The rep sees this.')
    .max(200, 'Required, 5–200 characters. The rep sees this.'),
});

export interface DaysValue {
  to?: Date | null;
  from?: Date | null;
}

/** The approved range picked, as day strings, or nulls. */
export const pickedDays = (days?: DaysValue) => ({
  endDate: toDay(days?.to ?? null),
  startDate: toDay(days?.from ?? null),
});

/** Whether a picked range is a valid "some days": inside and shorter. */
export const isSomeDays = (row: TimeOffRow, days?: DaysValue) => {
  const { endDate, startDate } = pickedDays(days);
  return Boolean(
    startDate &&
    endDate &&
    startDate >= row.startDate &&
    endDate <= row.endDate &&
    endDate >= startDate &&
    dayCount(startDate, endDate) < row.days
  );
};

export const fullRange = (row: TimeOffRow): DaysValue => ({
  to: dayToDate(row.endDate),
  from: dayToDate(row.startDate),
});

export const approvedRange = (row: TimeOffRow): DaysValue => ({
  to: dayToDate(row.approvedEndDate ?? row.endDate),
  from: dayToDate(row.approvedStartDate ?? row.startDate),
});

/** "Vanessa M. is on Holiday Market on Nov 13 …" (no he/she: the name). */
export const showsText = (row: TimeOffRow) => {
  const name = row.name || 'This rep';
  const shows = row.shows ?? [];
  if (!shows.length) {
    return null;
  }
  const one = (show: TimeOffRow['shows'][number]) => ({
    name: show.name,
    day: longRange(show.firstDate, show.firstDate, { dropYear: true }),
  });
  return { name, more: shows.length - 2, first: shows.slice(0, 2).map(one) };
};

export const decisionOptions: { label: string; value: TimeOffDecision }[] = [
  { value: 'approved', label: 'Approved' },
  { value: 'approvedSomeDays', label: 'Approve some days' },
  { value: 'notApproved', label: 'Not approved' },
];
